type RegistrationFailureKind = 'system' | 'restricted';

let currentRegistrationFailureKind: RegistrationFailureKind = 'system';

/**
 * Backend codes that mean the player was restricted (SEON ban, linked self-exclusion, or banned
 * at registration) rather than hitting a transient system / network failure. Matched
 * case-insensitively; extend this set as new restriction codes are introduced.
 */
const RESTRICTED_ERROR_CODES = new Set<string>(['banned_by_fraud']);

function normaliseErrorCode(value: unknown): string | undefined {
  return typeof value === 'string' ? value.trim().toLowerCase() : undefined;
}

function toResponsePayload(data: unknown): Record<string, unknown> | undefined {
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
}

function isRestrictedResponsePayload(data: unknown): boolean {
  const payload = toResponsePayload(data);
  if (!payload) {
    return false;
  }

  const code = normaliseErrorCode(payload.error) ?? normaliseErrorCode(payload.message);
  return code !== undefined && RESTRICTED_ERROR_CODES.has(code);
}

/**
 * Derives whether a registration failure is a fraud / SEON / self-exclusion restriction or a
 * generic system issue.
 *
 * Only a positive restriction signal routes to the "restricted" journey: a recognised restriction
 * code, or a 403 that carries a structured API error body (the account was created but blocked at
 * registration). Cloudflare blocks and network failures arrive as a 403/5xx with no parseable
 * body, so they intentionally fall through to the "system" screen.
 *
 * @param error - Original caught error, used to inspect the response payload.
 * @param statusCode - Optional HTTP status code from the failed request.
 */
function inferRegistrationFailureKind(error: unknown, statusCode?: number): RegistrationFailureKind {
  const responsePayload = toResponsePayload((error as { responseData?: unknown } | null)?.responseData);

  if (isRestrictedResponsePayload(responsePayload) || isRestrictedResponsePayload(error)) {
    return 'restricted';
  }

  if (statusCode === 403 && responsePayload !== undefined) {
    return 'restricted';
  }

  return 'system';
}

function setRegistrationFailureKind(kind: RegistrationFailureKind): void {
  currentRegistrationFailureKind = kind;
}

function getRegistrationFailureKind(): RegistrationFailureKind {
  return currentRegistrationFailureKind;
}

/** Clears any failure kind left over from a previous attempt; call when the flow (re)starts. */
function resetRegistrationFailureKind(): void {
  currentRegistrationFailureKind = 'system';
}

export { getRegistrationFailureKind, inferRegistrationFailureKind, isRestrictedResponsePayload, resetRegistrationFailureKind, setRegistrationFailureKind };
export type { RegistrationFailureKind };
